import { PublicRatingScope, OrganizerRating, EventRating } from 'components/ReviewAudit/PublicRatings'
import { organizerListPath } from "../listQuery"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { capitalize, isEmpty } from "lodash"
import moment from "moment"
import OsImpersonateAction from "../../OsImpersonateAction"
import ProfileLoadError from "../../ProfileLoadError"
import {
  Badge,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap"

import defaultProfilePicture from "../../../../assets/images/users/default-profile-picture.png"
import "../../../../assets/scss/custom/pages/_organizerAccounts.scss"

import Breadcrumbs from "components/Common/Breadcrumb"
import { getOrganizerInfo, getOwnerClub, getTournaments } from "../actions"
import { getClubs } from "../../ClubAccounts/api"
import { isSuperAdmin } from "helpers/cpAccessPolicy"
import {
  formatMembership,
  getOrganizerDisplayName,
  getOrganizerProfileImage,
  getOrganizerSportRows,
  getPaginationWindow,
  getTournamentRegistrationOpening,
  paginateItems,
} from "./profileDisplay"

const INITIAL_TOURNAMENT_OPTIONS = {
  page: 1,
  limit: 10,
  sortBy: "startDate:desc",
}

const ASSIGNED_CLUBS_PER_PAGE = 7

const DetailItem = ({ children, label, wide }) => (
  <div className={`organizer-profile-detail${wide ? " is-wide" : ""}`}>
    <div className="organizer-profile-detail-label">{label}</div>
    <div className="organizer-profile-detail-value">{children ?? "—"}</div>
  </div>
)

DetailItem.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string.isRequired,
  wide: PropTypes.bool,
}

DetailItem.defaultProps = {
  children: null,
  wide: false,
}

// One of the header's four labelled groups across the card's width.
const HeaderFact = ({ children, label }) => (
  <section className="organizer-profile-fact">
    <h4 className="organizer-profile-fact-label">{label}</h4>
    {children}
  </section>
)

HeaderFact.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
}

// A long address breaks before its @ first, never mid-word.
const EmailText = ({ email }) => {
  const at = email.lastIndexOf("@")

  if (at <= 0) return email

  return (
    <>
      {email.slice(0, at)}
      <wbr />
      {email.slice(at)}
    </>
  )
}

EmailText.propTypes = {
  email: PropTypes.string.isRequired,
}

const RegistrationOpening = ({ tournament }) => {
  const { opensAt, detail } = getTournamentRegistrationOpening(tournament)

  if (!opensAt) return "—"

  return (
    <>
      {opensAt}
      {detail && <div className="organizer-tournament-meta">{detail}</div>}
    </>
  )
}

RegistrationOpening.propTypes = {
  tournament: PropTypes.object.isRequired,
}

const SectionHeading = ({ children, description }) => (
  <div className="organizer-profile-section-heading">
    <CardTitle className="font-size-15 mb-1">{children}</CardTitle>
    {description && <p className="text-muted mb-0">{description}</p>}
  </div>
)

SectionHeading.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.string,
}

SectionHeading.defaultProps = {
  description: null,
}

const statusColor = status => {
  const normalizedStatus = String(status || "").toLowerCase()

  if (["activated", "active", "created", "enabled"].includes(normalizedStatus)) {
    return "success"
  }
  if (normalizedStatus === "pending") return "info"
  if (["blocked", "disabled"].includes(normalizedStatus)) return "warning"
  return "secondary"
}

export const OrganizerInfo = props => {
  const { t } = useTranslation('reviewAudit')
  const {
    organizerInfo,
    getOrganizerInfo,
    clubInfo,
    getOwnerClub,
    getTournaments,
    tournaments,
    profileLoading,
    profileError,
    tournamentsLoading,
    tournamentsError,
    userDetails,
    match: { params },
  } = props
  // The event's Logistics tab is the SuperAdmin's (cpAccessPolicy.js), so only a SuperAdmin gets its link.
  const canOpenLogistics = isSuperAdmin(userDetails)

  const [assignedClubs, setAssignedClubs] = useState([])
  const [assignedClubsPage, setAssignedClubsPage] = useState(1)
  const [locationsLoading, setLocationsLoading] = useState(false)
  const [tournamentOptions, setTournamentOptions] = useState(
    INITIAL_TOURNAMENT_OPTIONS
  )
  // Retry on a profile that could not be read opens it again: the organizer, then its first page of tournaments.
  const [profileAttempt, setProfileAttempt] = useState(0)

  useEffect(() => {
    setAssignedClubs([])
    setAssignedClubsPage(1)
    setTournamentOptions(INITIAL_TOURNAMENT_OPTIONS)
    getOrganizerInfo(params.id)
    getTournaments(params.id, INITIAL_TOURNAMENT_OPTIONS)
  }, [getOrganizerInfo, getTournaments, params.id, profileAttempt])

  useEffect(() => {
    if (organizerInfo?.role === "club") {
      getOwnerClub(organizerInfo.id)
    }
  }, [getOwnerClub, organizerInfo])

  const assignedClubIds = organizerInfo?.organizerInfo?.assignedClubs || []
  const assignedClubIdsKey = assignedClubIds.join(",")

  useEffect(() => {
    let active = true

    if (!assignedClubIdsKey) {
      setAssignedClubs([])
      setLocationsLoading(false)
      return () => {
        active = false
      }
    }

    const loadAssignedClubs = async () => {
      setLocationsLoading(true)

      try {
        const response = await getClubs(
          { ids: assignedClubIds },
          {
            page: 1,
            limit: Math.min(Math.max(assignedClubIds.length, 1), 1000),
            sortBy: "clubName:asc",
          }
        )

        if (active) {
          setAssignedClubs(response?.data?.results || [])
          setAssignedClubsPage(1)
        }
      } catch (error) {
        if (active) setAssignedClubs([])
      } finally {
        if (active) setLocationsLoading(false)
      }
    }

    loadAssignedClubs()

    return () => {
      active = false
    }
  }, [assignedClubIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTournamentPageChange = nextPage => {
    if (
      tournamentsLoading ||
      nextPage < 1 ||
      nextPage > (tournaments?.totalPages || 1) ||
      nextPage === tournamentOptions.page
    ) {
      return
    }

    const nextOptions = { ...tournamentOptions, page: nextPage }
    setTournamentOptions(nextOptions)
    getTournaments(params.id, nextOptions)
  }

  const displayName = getOrganizerDisplayName(organizerInfo, clubInfo)
  const assignedClubsPagination = paginateItems(
    assignedClubs,
    assignedClubsPage,
    ASSIGNED_CLUBS_PER_PAGE
  )
  const assignedClubsPaginationPages = getPaginationWindow(
    assignedClubsPagination.page,
    assignedClubsPagination.totalPages
  )
  const organizerLocation =
    organizerInfo?.organizerInfo?.location?.name ||
    organizerInfo?.location?.name ||
    clubInfo?.location?.name ||
    "Location not set"
  const membership = formatMembership(organizerInfo?.membership)
  const sportRows = getOrganizerSportRows(organizerInfo?.sports)
  const totalTournaments = tournaments?.totalResults || 0
  // The first read of the tournaments failed: their number is not known, so it is not shown as none.
  const tournamentsUnknown = tournamentsError && !tournaments?.results
  const tournamentPage = tournaments?.page || tournamentOptions.page
  const tournamentLimit = tournaments?.limit || tournamentOptions.limit
  const tournamentTotalPages = tournaments?.totalPages || 0
  const tournamentStart = totalTournaments
    ? (tournamentPage - 1) * tournamentLimit + 1
    : 0
  const tournamentEnd = Math.min(
    tournamentPage * tournamentLimit,
    totalTournaments
  )
  const paginationPages = getPaginationWindow(
    tournamentPage,
    tournamentTotalPages
  )
  const isPlayerOrganizer = organizerInfo?.role === "player"
  const managedClub = clubInfo?.clubInfo || clubInfo
  const editPath = isPlayerOrganizer
    ? `/accounts/players/${organizerInfo.id}/edit`
    : `/accounts/clubs/${clubInfo?.id}/edit`
  // Web has no organizer page: a player organizer's public page is its player profile, a club
  // organizer's is its club's page.
  const publicProfilePath = isPlayerOrganizer
    ? `/accounts/${organizerInfo.id}`
    : organizerInfo?.role === "club" && clubInfo?.id
      ? `/clubs/${clubInfo.id}`
      : null

  return (
    <React.Fragment>
      <div className="page-content organizer-profile-page">
        <Container fluid>
          <Breadcrumbs
            title="Organizers"
            path={organizerListPath(props.location?.state?.organizerListSearch)}
            breadcrumbItem="Organizer Profile"
          />

          {profileError ? (
            <ProfileLoadError title="Unable to load organizer profile" profile="organizer profile" status={profileError.status}
              onRetry={() => setProfileAttempt(attempt => attempt + 1)} />
          ) : profileLoading || isEmpty(organizerInfo) ? (
            <div className="text-center">
              <Spinner className="mt-5 mb-5" color="primary" />
            </div>
          ) : (
            <>
              <Card className="organizer-profile-hero">
                <CardBody>
                  <div className="organizer-profile-hero-main">
                    <div className="organizer-profile-identity">
                      <img
                        src={getOrganizerProfileImage(organizerInfo)}
                        alt=""
                        className="organizer-profile-avatar"
                        onError={event => {
                          event.currentTarget.onerror = null
                          event.currentTarget.src = defaultProfilePicture
                        }}
                      />
                      <div className="min-w-0">
                        <div className="organizer-profile-eyebrow">
                          Organizer #{organizerInfo.userId || "—"}
                        </div>
                        <h3 className="organizer-profile-name">{displayName}</h3>
                        <div className="organizer-profile-badges">
                          <Badge color={statusColor(organizerInfo.status)}>
                            {capitalize(organizerInfo.status || "unknown")}
                          </Badge>
                          <Badge color="soft-secondary">
                            {capitalize(organizerInfo.role || "unknown")} account
                          </Badge>
                          {isPlayerOrganizer && (
                            <Badge color="soft-secondary">
                              {capitalize(membership.plan)} membership
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="organizer-profile-actions">
                      {publicProfilePath ? (
                        <a
                          className="btn btn-primary"
                          href={`${process.env.REACT_APP_WEB_URL}${publicProfilePath}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          View public profile
                          <i className="mdi mdi-open-in-new ml-2" />
                        </a>
                      ) : null}
                      <OsImpersonateAction account={organizerInfo} />
                      {clubInfo?.id || isPlayerOrganizer ? (
                        <Link className="btn btn-light" to={editPath}>
                          Edit {isPlayerOrganizer ? "player account" : "club"}
                        </Link>
                      ) : null}
                    </div>
                  </div>

                  <div className="organizer-profile-facts">
                    <HeaderFact label="Contact">
                      {organizerInfo.email ? (
                        // the <wbr> would put a space in the link's name: the address is its name
                        <a
                          className="organizer-profile-contact-link"
                          href={`mailto:${organizerInfo.email}`}
                          aria-label={organizerInfo.email}
                        >
                          <i className="bx bx-envelope" aria-hidden="true" />
                          <span><EmailText email={organizerInfo.email} /></span>
                        </a>
                      ) : (
                        <span className="organizer-profile-contact-link text-muted">
                          <i className="bx bx-envelope" aria-hidden="true" />
                          <span>Email not set</span>
                        </span>
                      )}
                      <div className="organizer-profile-contact-link">
                        <i className="bx bx-map" aria-hidden="true" />
                        <span>{organizerLocation}</span>
                      </div>
                    </HeaderFact>
                    <HeaderFact label="Rating">
                      <PublicRatingScope kind="organizers" ids={[params.id]}><OrganizerRating id={params.id} /></PublicRatingScope>
                      <a className="organizer-profile-fact-link" href="#organizer-tournaments">{t('seeRatingByTournaments')}</a>
                    </HeaderFact>
                    <HeaderFact label="Organizer access">
                      <div className="organizer-profile-badges">
                        <Badge color="soft-primary">Organizer access</Badge>
                      </div>
                      <div className="organizer-profile-contact-link">
                        <i className="bx bx-building-house" aria-hidden="true" />
                        <span>
                          {assignedClubIds.length} assigned{" "}
                          {assignedClubIds.length === 1 ? "club" : "clubs"}
                        </span>
                      </div>
                    </HeaderFact>
                    <HeaderFact label="Tournaments">
                      <div className="organizer-profile-fact-value">
                        {tournamentsLoading && !totalTournaments ? "…" : tournamentsUnknown ? "—" : totalTournaments}
                      </div>
                      <a className="organizer-profile-fact-link" href="#organizer-tournaments">View tournaments</a>
                    </HeaderFact>
                  </div>
                </CardBody>
              </Card>

              <Row className="organizer-profile-overview-row">
                <Col xl="6" className="d-flex">
                  <Card className="organizer-profile-card flex-fill">
                    <CardBody>
                      <SectionHeading description="Organizer contact details and the underlying account.">
                        Organizer details
                      </SectionHeading>

                      <div className="organizer-profile-contact-card">
                        <div className="organizer-profile-contact-title">
                          Organizer contact
                        </div>
                        <div className="font-weight-semibold mb-2">
                          {[
                            organizerInfo?.organizerInfo?.firstName,
                            organizerInfo?.organizerInfo?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                            organizerInfo?.organizerInfo?.companyName ||
                            "Not set"}
                        </div>
                        {organizerInfo?.organizerInfo?.email ? (
                          <a
                            className="organizer-profile-contact-link"
                            href={`mailto:${organizerInfo.organizerInfo.email}`}
                          >
                            <i className="bx bx-envelope" aria-hidden="true" />
                            <span>{organizerInfo.organizerInfo.email}</span>
                          </a>
                        ) : (
                          <span className="organizer-profile-contact-link text-muted">
                            <i className="bx bx-envelope" aria-hidden="true" />
                            Email not set
                          </span>
                        )}
                        {organizerInfo?.organizerInfo?.phone ? (
                          <a
                            className="organizer-profile-contact-link"
                            href={`tel:${organizerInfo.organizerInfo.phone}`}
                          >
                            <i className="bx bx-phone" aria-hidden="true" />
                            <span>{organizerInfo.organizerInfo.phone}</span>
                          </a>
                        ) : (
                          <span className="organizer-profile-contact-link text-muted">
                            <i className="bx bx-phone" aria-hidden="true" />
                            Phone not set
                          </span>
                        )}
                      </div>

                      <div className="organizer-profile-divider" />

                      {isPlayerOrganizer ? (
                        <>
                          <div className="organizer-profile-subheading">
                            Player account
                            <Link
                              to={`/accounts/players/${organizerInfo.id}`}
                              className="font-size-12"
                            >
                              Open player profile
                              <i className="mdi mdi-arrow-right ml-1" />
                            </Link>
                          </div>
                          <div className="organizer-profile-detail-grid">
                            <DetailItem label="Birth date">
                              {organizerInfo.birthDate
                                ? moment(organizerInfo.birthDate).format(
                                    "D MMM YYYY"
                                  )
                                : "—"}
                            </DetailItem>
                            <DetailItem label="Membership">
                              {capitalize(membership.plan)}
                              {membership.endDate
                                ? ` · until ${membership.endDate}`
                                : ""}
                            </DetailItem>
                            <DetailItem label="Phone">
                              {organizerInfo.phone || "—"}
                            </DetailItem>
                            <DetailItem label="Gender">
                              {capitalize(organizerInfo.gender || "—")}
                            </DetailItem>
                            <DetailItem label="Dominant hand">
                              {capitalize(organizerInfo.dominantHand || "—")}
                            </DetailItem>
                            <DetailItem label="T-shirt size">
                              {organizerInfo.tShirtSize?.toUpperCase() || "—"}
                            </DetailItem>
                          </div>

                          <div className="organizer-profile-divider" />
                          <div className="organizer-profile-contact-title">
                            Competitive levels
                          </div>
                          {sportRows.length ? (
                            <div className="organizer-profile-sports">
                              {sportRows.map(sport => (
                                <div
                                  className="organizer-profile-sport-row"
                                  key={sport.sport}
                                >
                                  <strong>{capitalize(sport.sport)}</strong>
                                  <span>
                                    Singles {sport.singles ?? "—"}
                                  </span>
                                  <span>
                                    Doubles {sport.doubles ?? "—"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="organizer-profile-empty compact">
                              No competitive levels are configured.
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="organizer-profile-subheading">
                            Club account
                            {clubInfo?.id && (
                              <Link
                                to={`/accounts/clubs/${clubInfo.id}`}
                                className="font-size-12"
                              >
                                Open club profile
                                <i className="mdi mdi-arrow-right ml-1" />
                              </Link>
                            )}
                          </div>
                          <div className="organizer-profile-detail-grid">
                            <DetailItem label="Club">
                              {managedClub?.clubName || displayName}
                            </DetailItem>
                            <DetailItem label="Club ID">
                              {clubInfo?.clubId || "—"}
                            </DetailItem>
                            <DetailItem label="Company">
                              {organizerInfo?.organizerInfo?.companyName || "—"}
                            </DetailItem>
                            <DetailItem label="Registration number">
                              {organizerInfo?.organizerInfo
                                ?.uniqueRegistrationNumber || "—"}
                            </DetailItem>
                            <DetailItem label="Owner" wide>
                              {[organizerInfo.firstName, organizerInfo.lastName]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </DetailItem>
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>

                </Col>

                <Col xl="6" className="d-flex">
                  <Card className="organizer-profile-card organizer-profile-clubs-card flex-fill">
                    <CardBody>
                      <div className="organizer-profile-card-heading">
                        <SectionHeading description="Clubs where this organizer can create and manage events.">
                          Assigned clubs
                        </SectionHeading>
                        <Badge color="soft-secondary" className="px-2 py-2">
                          {assignedClubIds.length}
                        </Badge>
                      </div>

                      {locationsLoading ? (
                        <div className="text-center py-4">
                          <Spinner size="sm" color="primary" />
                        </div>
                      ) : assignedClubs.length ? (
                        <div className="organizer-profile-club-list">
                          {assignedClubsPagination.items.map(club => (
                            <div className="organizer-profile-club" key={club.id}>
                              <span className="organizer-profile-club-icon">
                                <i className="bx bx-building-house" />
                              </span>
                              <div className="organizer-profile-club-content">
                                <Link
                                  className="font-weight-semibold organizer-profile-club-name"
                                  to={`/accounts/clubs/${club.id}`}
                                >
                                  {club.clubName || club.businessName}
                                </Link>
                                <div className="text-muted font-size-12 organizer-profile-club-location">
                                  {club.location?.name || "Location not set"}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="organizer-profile-empty">
                          <i className="bx bx-map" aria-hidden="true" />
                          <div>No clubs are assigned to this organizer.</div>
                        </div>
                      )}

                      {assignedClubsPagination.totalPages > 1 && (
                        <div className="organizer-profile-club-pagination">
                          <span>
                            Showing {assignedClubsPagination.start}–
                            {assignedClubsPagination.end} of {assignedClubs.length}
                          </span>
                          <Pagination
                            className="pagination-rounded mb-0"
                            size="sm"
                            aria-label="Assigned club pages"
                          >
                            <PaginationItem
                              disabled={assignedClubsPagination.page <= 1}
                            >
                              <PaginationLink
                                previous
                                href="#"
                                aria-label="Previous assigned club page"
                                onClick={event => {
                                  event.preventDefault()
                                  setAssignedClubsPage(
                                    assignedClubsPagination.page - 1
                                  )
                                }}
                              />
                            </PaginationItem>
                            {assignedClubsPaginationPages.map(page => (
                              <PaginationItem
                                active={page === assignedClubsPagination.page}
                                key={page}
                              >
                                <PaginationLink
                                  href="#"
                                  onClick={event => {
                                    event.preventDefault()
                                    setAssignedClubsPage(page)
                                  }}
                                >
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                            <PaginationItem
                              disabled={
                                assignedClubsPagination.page >=
                                assignedClubsPagination.totalPages
                              }
                            >
                              <PaginationLink
                                next
                                href="#"
                                aria-label="Next assigned club page"
                                onClick={event => {
                                  event.preventDefault()
                                  setAssignedClubsPage(
                                    assignedClubsPagination.page + 1
                                  )
                                }}
                              />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col xl="12">
                  <Card id="organizer-tournaments" className="organizer-profile-card organizer-tournaments-card">
                    <CardBody>
                      <div className="organizer-profile-card-heading">
                        <SectionHeading description="All non-deleted tournaments owned by this organizer, newest tournament date first.">
                          Tournaments
                        </SectionHeading>
                        {!tournamentsUnknown && (
                          <Badge color="soft-primary" className="px-2 py-2">
                            {totalTournaments} total
                          </Badge>
                        )}
                      </div>

                      {tournamentsLoading ? (
                        <div className="text-center organizer-profile-table-loading">
                          <Spinner color="primary" />
                        </div>
                      ) : tournamentsError ? (
                        <div className="alert alert-danger mb-0" role="alert">
                          Could not load tournaments.{" "}
                          <button type="button" className="btn btn-link p-0 align-baseline"
                            onClick={() => getTournaments(params.id, tournamentOptions)}>
                            Retry
                          </button>
                        </div>
                      ) : tournaments?.results?.length ? (
                        <>
                          <PublicRatingScope kind="events" ids={(tournaments?.results || []).map(row => row._id || row.id)}>
                          <div className="table-responsive organizer-profile-tournaments-table">
                            <table className="table table-centered table-hover mb-0">
                              <thead>
                                <tr>
                                  <th>Tournament</th>
                                  <th>Start</th>
                                  <th>End</th>
                                  <th>Registration opens</th>
                                  <th className="text-center">Participants</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tournaments.results.map(tournament => (
                                  <tr key={tournament._id || tournament.id}>
                                    <td>
                                      <Link
                                        className="organizer-tournament-name"
                                        to={`/events/${
                                          tournament._id || tournament.id
                                        }/details`}
                                      >
                                        {tournament.name || "Unnamed tournament"}
                                      </Link>
                                      <div><EventRating id={tournament._id || tournament.id} /></div>
                                      <div className="organizer-tournament-meta">
                                        {tournament.eventId
                                          ? `#${tournament.eventId}`
                                          : "No public ID"}
                                        {tournament.category
                                          ? ` · ${capitalize(
                                              tournament.category
                                            )}`
                                          : ""}
                                        {canOpenLogistics && (
                                          <>
                                            {" · "}
                                            <Link
                                              to={`/events/${
                                                tournament._id || tournament.id
                                              }/logistics`}
                                              aria-label={`Logistics of ${
                                                tournament.name ||
                                                "Unnamed tournament"
                                              }`}
                                            >
                                              Logistics
                                            </Link>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      {tournament.startDate
                                        ? moment(tournament.startDate).format(
                                            "D MMM YYYY"
                                          )
                                        : "—"}
                                    </td>
                                    <td>
                                      {tournament.endDate
                                        ? moment(tournament.endDate).format(
                                            "D MMM YYYY"
                                          )
                                        : "—"}
                                    </td>
                                    <td>
                                      <RegistrationOpening
                                        tournament={tournament}
                                      />
                                    </td>
                                    <td className="text-center font-weight-semibold">
                                      {tournament.participants ?? 0}
                                    </td>
                                    <td>
                                      <Badge color={statusColor(tournament.status)}>
                                        {capitalize(tournament.status || "unknown")}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          </PublicRatingScope>

                          <div className="organizer-profile-pagination">
                            <span>
                              Showing {tournamentStart}–{tournamentEnd} of{" "}
                              {totalTournaments}
                            </span>
                            {tournamentTotalPages > 1 && (
                              <Pagination
                                className="pagination-rounded mb-0"
                                size="sm"
                                aria-label="Tournament pages"
                              >
                                <PaginationItem disabled={tournamentPage <= 1}>
                                  <PaginationLink
                                    previous
                                    href="#"
                                    aria-label="Previous tournament page"
                                    onClick={event => {
                                      event.preventDefault()
                                      handleTournamentPageChange(
                                        tournamentPage - 1
                                      )
                                    }}
                                  />
                                </PaginationItem>
                                {paginationPages.map(page => (
                                  <PaginationItem
                                    active={page === tournamentPage}
                                    key={page}
                                  >
                                    <PaginationLink
                                      href="#"
                                      onClick={event => {
                                        event.preventDefault()
                                        handleTournamentPageChange(page)
                                      }}
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                ))}
                                <PaginationItem
                                  disabled={
                                    tournamentPage >= tournamentTotalPages
                                  }
                                >
                                  <PaginationLink
                                    next
                                    href="#"
                                    aria-label="Next tournament page"
                                    onClick={event => {
                                      event.preventDefault()
                                      handleTournamentPageChange(
                                        tournamentPage + 1
                                      )
                                    }}
                                  />
                                </PaginationItem>
                              </Pagination>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="organizer-profile-empty">
                          <i className="bx bx-trophy" aria-hidden="true" />
                          <div>No tournaments belong to this organizer.</div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

OrganizerInfo.propTypes = {
  clubInfo: PropTypes.object,
  getOrganizerInfo: PropTypes.func,
  getOwnerClub: PropTypes.func,
  getTournaments: PropTypes.func,
  match: PropTypes.object,
  organizerInfo: PropTypes.object,
  profileError: PropTypes.object,
  profileLoading: PropTypes.bool,
  tournaments: PropTypes.object,
  tournamentsError: PropTypes.bool,
  tournamentsLoading: PropTypes.bool,
  userDetails: PropTypes.object,
}

const mapStateToProps = ({ organizers, session }) => ({
  profileLoading: organizers.profileLoading,
  profileError: organizers.profileError,
  tournamentsLoading: organizers.tournamentsLoading,
  tournamentsError: organizers.tournamentsError,
  organizerInfo: organizers.organizerInfo,
  clubInfo: organizers.organizerClubInfo,
  tournaments: organizers.tournaments,
  userDetails: session?.userDetails,
})

const mapDispatchToProps = dispatch => ({
  getOrganizerInfo: id => dispatch(getOrganizerInfo(id)),
  getOwnerClub: id => dispatch(getOwnerClub(id)),
  getTournaments: (id, options) => dispatch(getTournaments(id, options)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(OrganizerInfo))
